import { useCallback, type FormEvent } from 'react';
import { Link, Redirect } from 'react-router-dom';
import { Button, Form } from 'react-bootstrap';
import { connect } from 'react-redux';
import type { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';
import { RouterPaths } from '../../../app/appConstants';
import { Loader } from '../../../components/Loader/Loader.jsx';
import FormPasswordEye from '../../../components/FormPasswordEye/FormPasswordEye.jsx';
import { useLangContext } from '../../../providers/LangProvider/LangProviderContext.js';
import { loginAction } from '../authActions.js';
import { getAppLoggedIn, getAppLoggedInProcess, getAppLoginError } from '../authSelectors.js';
import AuthWrapper from './AuthWrapper';
import { LoginForgotPasswordLinkContainerStyled, LoginStyled } from './styled/LoginStyled';

interface LoginProps {
    loggedIn?: boolean;
    loggedInProcess?: boolean;
    loginError?: string | boolean | null;
    loginAction: (payload: { login: string; password: string }) => void;
}

function Login({ loggedIn, loggedInProcess, loginError, loginAction: dispatchLogin }: LoginProps) {
    const { getLangPath } = useLangContext();

    const submitHandler = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            const form = e.currentTarget;
            const emailInput = form.elements.namedItem('email') as HTMLInputElement;
            const passwordInput = form.elements.namedItem('password') as HTMLInputElement;
            dispatchLogin({
                login: emailInput.value,
                password: passwordInput.value,
            });
        },
        [dispatchLogin],
    );

    if (loggedIn) {
        return <Redirect to={RouterPaths.main} />;
    }

    return (
        <AuthWrapper>
            <LoginStyled>
                <p className="section-eyebrow">{getLangPath('auth.brand')}</p>
                <h1 className="auth-title">{getLangPath('auth.title_login')}</h1>
                <p className="auth-subtitle">{getLangPath('auth.subtitle_login')}</p>

                <Form onSubmit={submitHandler}>
                    <Form.Group className="mb-3" controlId="login-email">
                        <Form.Label>{getLangPath('auth.field_login')}</Form.Label>
                        <Form.Control
                            name="email"
                            type="email"
                            placeholder={getLangPath('auth.field_login')}
                            isInvalid={!!loginError}
                            required
                            autoFocus
                        />
                    </Form.Group>

                    <Form.Group className="mb-2" controlId="login-password">
                        <Form.Label>{getLangPath('auth.field_password')}</Form.Label>
                        <FormPasswordEye
                            name="password"
                            placeholder={getLangPath('auth.field_password')}
                            isInvalid={!!loginError}
                            required
                        />
                        {!!loginError && (
                            <Form.Control.Feedback type="invalid">
                                {getLangPath(typeof loginError === 'string' ? loginError : 'error.default_message')}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>

                    <LoginForgotPasswordLinkContainerStyled>
                        <Link to={RouterPaths.forgot}>{getLangPath('auth.link_forgot_password')}</Link>
                    </LoginForgotPasswordLinkContainerStyled>

                    <div className="auth-actions">
                        <Button variant="primary" size="lg" type="submit">
                            {getLangPath('auth.login_submit')}
                        </Button>
                        <Button variant="outline-info" className="btn-md" as={Link} to={RouterPaths.register}>
                            {getLangPath('auth.link_to_register')}
                        </Button>
                    </div>
                </Form>
                <Loader show={!!loggedInProcess} bg={undefined} size={undefined} />
            </LoginStyled>
        </AuthWrapper>
    );
}

const mapStateToProps = createStructuredSelector({
    loggedIn: getAppLoggedIn,
    loggedInProcess: getAppLoggedInProcess,
    loginError: getAppLoginError,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    loginAction: (payload: { login: string; password: string }) => dispatch(loginAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Login);
